"use client";

import Image from "next/image";
import React from "react";
import { motion } from "framer-motion";
import { useSectionInView } from "@/lib/hooks";
import { profile, companies, currentCompany } from "@/lib/data";

export default function Intro() {
  const { ref } = useSectionInView("Home", 0.5);

  return (
    <section
      ref={ref}
      id="home"
      className="mb-28 max-w-[50rem] text-center sm:mb-0 scroll-mt-[100rem]"
    >
      <div className="flex items-center justify-center">
        <div className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              type: "tween",
              duration: 0.2,
            }}
          >
            <Image
              src={profile.avatar}
              alt="Ketan portrait"
              width="192"
              height="192"
              quality="95"
              priority={true}
              className="h-52 w-52 rounded-full object-cover border-[0.35rem] border-white shadow-xl"
            />
          </motion.div>

          <motion.span
            className="absolute bottom-5 right-5 text-4xl"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 125,
              delay: 0.1,
              duration: 0.7,
            }}
          >
            👋
          </motion.span>
        </div>
      </div>

      <motion.h1
        className="mb-10 mt-4 px-4 text-lg font-medium text-gray-400 !leading-[1.5] sm:text-2xl"
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <span className="font-bold text-white text-2xl sm:text-4xl">Hello, I'm {profile.name}.</span>
        <motion.span
          className="text-4xl"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            type: "spring",
            stiffness: 125,
            delay: 0.1,
            duration: 0.7,
          }}
        >
          👋
        </motion.span>
        <br />
        <motion.span
          className="text-2xl"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            type: "spring",
            stiffness: 125,
            delay: 0.1,
            duration: 0.7,
          }}
        >
          👉
        </motion.span>{" "}
        I'm a <span className="font-bold">{profile.title}</span> specializing in creating robust{" "}
        <span className="font-bold">end-to-end applications</span> with {profile.experience} of experience.
      </motion.h1>

      <motion.div
        className="flex flex-col items-center gap-6 mt-4"
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        {/* Current Company */}
        <div className="flex flex-col items-center gap-3">
          <p className="text-sm text-gray-400 uppercase tracking-wider">Currently working at</p>
          <motion.a
            href={currentCompany.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 px-5 py-3 bg-white/10 border-2 border-white/20 rounded-lg hover:bg-white/15 hover:border-white/30 transition-all cursor-pointer"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.05 }}
          >
            <div className="relative w-10 h-10 flex-shrink-0">
              <Image
                src={currentCompany.logo}
                alt={`${currentCompany.name} logo`}
                fill
                className="object-contain rounded-md"
              />
            </div>
            <span className="text-base font-medium text-white transition-colors whitespace-nowrap">
              {currentCompany.name}
            </span>
          </motion.a>
        </div>

        {/* Past Companies */}
        <div className="flex flex-col items-center gap-3 mt-6">
          <p className="text-sm text-gray-400 uppercase tracking-wider">Worked with</p>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            {companies.map((company: typeof companies[number], index: number) => (
              <motion.a
                key={index}
                href={company.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 px-4 py-2 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 hover:border-white/20 transition-all cursor-pointer"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="relative w-8 h-8 flex-shrink-0">
                  <Image
                    src={company.logo}
                    alt={`${company.name} logo`}
                    fill
                    className="object-contain rounded-md"
                  />
                </div>
                <span className="text-sm text-gray-200 group-hover:text-white transition-colors whitespace-nowrap">
                  {company.name}
                </span>
              </motion.a>
            ))}
          </div>
        </div>
      </motion.div>

    </section>
  );
}
